// Fleet Reports — controls bar (2026-07-14; custom timeframe 2026-07-27).
// Lives in the always-mounted layout shell, ABOVE the <Switch> — not inside a
// routed sub-tab, or it would remount (and lose its state) on every tab change.
//
// Deliberately NO solution/service selector: the tenant has two services (CPE
// Monitoring and the fleet service) and only the fleet one has vehicles. The
// backend pins serviceId from the license, so offering a dropdown here would
// only invite mis-scoping. The fleet service is shown read-only.
//
// Timeframe picker: presets (this week / last N days / trailing complete weeks)
// PLUS a bounded custom range. All windows are opaque YYYY-MM-DD day keys anchored
// on the SERVER's dates (currentWindow.endDate = the tenant's "today", and
// earliestAvailable = the retention floor) — never a browser-timezone `new Date()`,
// so the window can't shift for a viewer in another zone. The custom range uses
// native <input type="date"> for exactly this reason: it yields a plain YYYY-MM-DD
// string with NO Date-object timezone math, and its min/max bound the picker.
import React from "react";
import { Card, Button, Select, MenuItem, FormControl } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import keys from "Keys";

const DAY_MS = 86400000;
const iso = (d) => d.toISOString().slice(0, 10);
const CUSTOM = "__custom__";
const daysBetween = (a, b) => Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / DAY_MS) + 1;

// Build the preset options: "this week so far", rolling "last N days" (ending the
// server's today, clamped to the retention floor), then the trailing complete
// Mon-Sun weeks. Pure UTC arithmetic on the opaque day keys.
const buildTimeframeOptions = (config, count = 6) => {
  const { defaultWindow, earliestAvailable, currentWindow } = config;
  const floor = earliestAvailable ? new Date(`${earliestAvailable}T00:00:00Z`).getTime() : -Infinity;
  const out = [];
  const serverToday = currentWindow && currentWindow.endDate; // tenant "today" (server-computed)
  // The in-progress week first — users must not wait for Sunday to see the week so far.
  if (currentWindow && currentWindow.startDate) out.push({ ...currentWindow, label: "This week so far" });
  // Rolling "last N days" ending today (inclusive), clamped to the retention floor.
  if (serverToday) {
    for (const n of [7, 14, 30]) {
      const startMs = new Date(`${serverToday}T00:00:00Z`).getTime() - (n - 1) * DAY_MS;
      const start = startMs < floor ? new Date(floor) : new Date(startMs);
      out.push({ startDate: iso(start), endDate: serverToday, label: `Last ${n} days` });
    }
  }
  // Trailing COMPLETE weeks (Mon-Sun) from the server default window, bounded on
  // earliestAvailable (the real TTL horizon) so we never offer a week the API 400s.
  if (defaultWindow && defaultWindow.startDate) {
    const base = new Date(`${defaultWindow.startDate}T00:00:00Z`);
    for (let i = 0; i < count; i++) {
      const start = new Date(base.getTime() - i * 7 * DAY_MS);
      if (start.getTime() < floor) break;
      const end = new Date(start.getTime() + 6 * DAY_MS);
      out.push({ startDate: iso(start), endDate: iso(end) });
    }
  }
  // De-dupe by window key, keeping the FIRST (labeled) option. Distinct presets can
  // coincide — e.g. on the last day of the week "This week so far" == "Last 7 days",
  // and near the retention floor two "Last N days" can clamp to the same range — which
  // would otherwise collide on both the React key and the MUI value.
  const seen = new Set();
  return out.filter((o) => {
    const k = `${o.startDate}|${o.endDate}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

export default function Controls({ config, startDate, endDate, onChange, onLoad, isFetching, loaded, canDownload }) {
  const options = buildTimeframeOptions(config);
  const maxWindowDays = config.maxWindowDays || 31;
  const minDate = config.earliestAvailable || null; // retention floor (server)
  const maxDate = (config.currentWindow && config.currentWindow.endDate) || null; // tenant "today" (server)

  // The current window's key, and whether it matches a listed preset. A window that
  // matches no preset (a custom range) drives the Select to the "Custom range…" item
  // so MUI never warns about an out-of-range value.
  const windowKey = startDate && endDate ? `${startDate}|${endDate}` : "";
  const matchesPreset = options.some((o) => `${o.startDate}|${o.endDate}` === windowKey);
  const [customOpen, setCustomOpen] = React.useState(false);
  const showCustom = customOpen || (!!windowKey && !matchesPreset);
  const selectValue = showCustom ? CUSTOM : windowKey;

  // Custom-range inputs (local; the report loads only on Apply, never on keystroke,
  // so adjusting two dates fires ONE request). Seeded from the current window when
  // custom mode is entered.
  const [cStart, setCStart] = React.useState("");
  const [cEnd, setCEnd] = React.useState("");
  const [customErr, setCustomErr] = React.useState("");
  React.useEffect(() => {
    if (showCustom) {
      setCStart((s) => s || startDate || minDate || "");
      setCEnd((e) => e || endDate || maxDate || "");
    }
    // Only re-seed on entering custom mode; user edits are preserved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCustom]);

  const onSelect = (v) => {
    setCustomErr("");
    if (v === CUSTOM) {
      // Seed from the current window and reveal the inputs — DON'T load yet.
      setCStart(startDate || minDate || "");
      setCEnd(endDate || maxDate || "");
      setCustomOpen(true);
      return;
    }
    setCustomOpen(false);
    const [s, en] = v.split("|");
    onChange({ startDate: s, endDate: en });
  };

  const applyCustom = () => {
    setCustomErr("");
    if (!cStart || !cEnd) return setCustomErr("Pick a start and end date.");
    if (cStart > cEnd) return setCustomErr("Start date must be on or before the end date.");
    if (minDate && cStart < minDate) return setCustomErr(`Start date can't be before ${minDate}. Telemetry is retained ${maxWindowDays} days.`);
    if (maxDate && cEnd > maxDate) return setCustomErr(`End date can't be after ${maxDate}.`);
    const days = daysBetween(cStart, cEnd);
    if (days > maxWindowDays) return setCustomErr(`That's ${days} days. The maximum window is ${maxWindowDays} days.`);
    onChange({ startDate: cStart, endDate: cEnd });
  };

  // Server-side .xlsx of the SAME report (GET /aggregation/fleet-report/export).
  // A raw fetch, NOT an RTK Query endpoint: the response is a binary workbook and
  // fetchBaseQuery would try to JSON-parse it. The token is re-read from
  // localStorage at click time (it may have refreshed since mount). This path
  // does NOT auto-refresh on 401/403 the way baseQueryWithReauth does — a rare
  // expiry surfaces as a friendly error and the user reloads; the report query on
  // this very page keeps the token fresh in practice.
  const [downloading, setDownloading] = React.useState(false);
  const [downloadError, setDownloadError] = React.useState("");
  const onDownload = async () => {
    setDownloadError("");
    setDownloading(true);
    try {
      const token = window.localStorage.getItem("token");
      const base = keys.baseUrl && keys.baseUrl.endsWith("/") ? keys.baseUrl : `${keys.baseUrl || ""}/`;
      const res = await fetch(`${base}aggregation/fleet-report/export?startDate=${startDate}&endDate=${endDate}`, {
        method: "GET",
        headers: { "x-access-token": token },
      });
      if (!res.ok) {
        let msg =
          res.status === 429
            ? "Too many downloads. Please wait a moment and try again."
            : res.status === 403
            ? "Fleet reporting is not enabled for your account."
            : res.status === 401
            ? "Your session has expired. Please reload the page."
            : "The download could not be built. Please try again.";
        // The server sends a JSON error body for these; prefer its message when present.
        try {
          const j = await res.json();
          if (j && j.message) msg = j.message;
        } catch (_) {
          /* not JSON — keep the status-based message */
        }
        setDownloadError(msg);
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `fleet-report_${startDate}_${endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (_) {
      setDownloadError("The download could not be built. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const dateInput = {
    padding: "7px 8px",
    fontSize: "14px",
    border: "1px solid #cccccc",
    borderRadius: "4px",
    color: "#444444",
  };

  return (
    <Card style={{ padding: "16px", marginBottom: "20px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
        <div>
          <p style={{ fontSize: "13px", color: "grey", margin: "0 0 4px 0" }}>Solution</p>
          {/* Served by /fleet-config from the licence's serviceId — never hardcoded.
              The id itself does not cross the wire; only its display name does. */}
          <p style={{ fontSize: "15px", fontWeight: "bold", color: "#666666", margin: 0 }}>
            {config.serviceName || "Fleet"}
          </p>
        </div>
        <div>
          <p style={{ fontSize: "13px", color: "grey", margin: "0 0 4px 0" }}>Timeframe</p>
          <FormControl size="small" style={{ minWidth: "240px" }}>
            <Select value={selectValue} displayEmpty onChange={(e) => onSelect(e.target.value)}>
              {options.length === 0 ? <MenuItem value="">No timeframes available</MenuItem> : null}
              {options.map((o) => (
                <MenuItem key={`${o.startDate}|${o.endDate}`} value={`${o.startDate}|${o.endDate}`}>
                  {o.label ? `${o.label} (${o.startDate} → ${o.endDate})` : `${o.startDate} → ${o.endDate}`}
                </MenuItem>
              ))}
              <MenuItem value={CUSTOM}>Custom range…</MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Bounded custom range — native date inputs (timezone-safe: plain YYYY-MM-DD,
            no Date-object math), applied explicitly so it fires one request. */}
        {showCustom ? (
          <div>
            <p style={{ fontSize: "13px", color: "grey", margin: "0 0 4px 0" }}>Custom range</p>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="date"
                aria-label="Start date"
                style={dateInput}
                value={cStart}
                min={minDate || undefined}
                max={cEnd || maxDate || undefined}
                onChange={(e) => setCStart(e.target.value)}
              />
              <span style={{ color: "grey" }}>→</span>
              <input
                type="date"
                aria-label="End date"
                style={dateInput}
                value={cEnd}
                min={cStart || minDate || undefined}
                max={maxDate || undefined}
                onChange={(e) => setCEnd(e.target.value)}
              />
              <Button variant="contained" color="secondary" size="small" onClick={applyCustom} disabled={isFetching} style={{ height: "37px" }}>
                Apply
              </Button>
            </div>
          </div>
        ) : null}

        <Button
          variant="contained"
          color="secondary"
          disabled={!startDate || !endDate || isFetching}
          onClick={onLoad}
          style={{ marginTop: "18px" }}
        >
          {isFetching ? "Loading…" : loaded ? "Reload" : "Load"}
        </Button>
        {/* Download the loaded report as .xlsx. Enabled only once a report is on
            screen (canDownload) so the button never offers a file for a window
            that has not been built. */}
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<DownloadIcon />}
          disabled={!canDownload || downloading}
          onClick={onDownload}
          style={{ marginTop: "18px" }}
        >
          {downloading ? "Preparing…" : "Download .xlsx"}
        </Button>
        <p style={{ fontSize: "13px", color: "grey", margin: "18px 0 0 auto" }}>
          Telemetry is retained for {maxWindowDays} days.
        </p>
      </div>
      {customErr ? <p style={{ fontSize: "13px", color: "#c0392b", margin: "10px 0 0 0" }}>{customErr}</p> : null}
      {downloadError ? <p style={{ fontSize: "13px", color: "#c0392b", margin: "10px 0 0 0" }}>{downloadError}</p> : null}
    </Card>
  );
}
